import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChartLineDown,
  faFaceSmile,
  faRobot,
  faHandshake,
  faRoute,
  faHandshakeSlash,
} from '@fortawesome/pro-solid-svg-icons';
type StrategyButton = {
  label: string;
  value: string;
  description: string;
  icon: IconProp;
  disabled: boolean;
  disabledReason?: string;
};

export const STRATEGY_COLORS = [
  { dot: 'bg-orange-500', pill: 'bg-orange-100 text-orange-700' },
  { dot: 'bg-teal-500', pill: 'bg-teal-100 text-teal-700' },
  { dot: 'bg-purple-500', pill: 'bg-purple-100 text-purple-700' },
  { dot: 'bg-sky-500', pill: 'bg-sky-100 text-sky-700' },
  { dot: 'bg-pink-500', pill: 'bg-pink-100 text-pink-700' },
  { dot: 'bg-green-500', pill: 'bg-green-100 text-green-700' },
  { dot: 'bg-red-500', pill: 'bg-red-100 text-red-700' },
] as const;

export type StrategyColor = (typeof STRATEGY_COLORS)[number];

export const STRATEGY_TYPES = {
  SCOUT_LIST: 'SCOUT_LIST',
  FRESH_FACES: 'FRESH_FACES',
  REFERRALS: 'REFERRALS',
  NOT_REFERRING: 'NOT_REFERRING',
  PATIENT_JOURNEY: 'PATIENT_JOURNEY',
  TREATING_VOLUME: 'TREATING_VOLUME',
} as const;

export const STRATEGY_NAMEMAPPING = {
  [STRATEGY_TYPES.SCOUT_LIST]: 'Scout List',
  [STRATEGY_TYPES.FRESH_FACES]: 'Fresh Faces',
  [STRATEGY_TYPES.REFERRALS]: 'Referrals',
  [STRATEGY_TYPES.NOT_REFERRING]: 'Radius-Based Analysis',
  [STRATEGY_TYPES.PATIENT_JOURNEY]: 'Patient Journey',
  [STRATEGY_TYPES.TREATING_VOLUME]: 'Treating Volume',
};

const STRATEGY_BUTTONS_UNSORTED: StrategyButton[] = [
  {
    label: 'Scout List',
    value: STRATEGY_TYPES.SCOUT_LIST,
    description:
      'Create a strategy using AI to generate targeted prospects based on your criteria',
    icon: faRobot,
    disabled: false,
  },
  // {
  //   label: 'Code Set',
  //   value: STRATEGY_TYPES.CODE_SET,
  //   description: 'Build a strategy using pre-existing code sets or filter sets',
  //   icon: faSearch,
  //   disabled: true,
  //   disabledReason: 'Coming Soon',
  // },
  {
    label: 'Fresh Faces',
    value: STRATEGY_TYPES.FRESH_FACES,
    description:
      'Show the up and comers for the codes you care about in your region.',
    icon: faFaceSmile,
    disabled: false,
    disabledReason: 'Coming Soon',
  },
  // {
  //   label: 'Top Billers',
  //   value: STRATEGY_TYPES.TOP_BILLERS,
  //   description:
  //     'Show the top billers for the codes you care about in your region.',
  //   icon: faMoneyBillTrendUp,
  //   disabled: true,
  //   disabledReason: 'Coming Soon',
  // },
  // {
  //   label: 'Top Movers',
  //   value: STRATEGY_TYPES.TOP_MOVERS,
  //   description:
  //     'Show the top movers for the codes you care about in your region.',
  //   icon: faBulldozer,
  //   disabled: true,
  //   disabledReason: 'Coming Soon',
  // },
  // {
  //   label: 'Top Decliners',
  //   value: STRATEGY_TYPES.TOP_DECLINERS,
  //   description:
  //     'Show the top decliners for the codes you care about in your region.',
  //   icon: faChartLineDown,
  //   disabled: true,
  //   disabledReason: 'Coming Soon',
  // },
];

export const STRATEGY_BUTTONS: StrategyButton[] =
  STRATEGY_BUTTONS_UNSORTED.sort((a, b) => {
    // Scout List always comes first
    if (a.value === STRATEGY_TYPES.SCOUT_LIST) return -1;
    if (b.value === STRATEGY_TYPES.SCOUT_LIST) return 1;

    // Then sort by enabled (false) before disabled (true)
    if (a.disabled !== b.disabled) {
      return a.disabled ? 1 : -1;
    }

    // If both have same disabled status, maintain original order
    return 0;
  });

const ENGAGE_STRATEGY_BUTTONS_UNSORTED: StrategyButton[] = [
  {
    label: 'Referrals',
    value: STRATEGY_TYPES.REFERRALS,
    description:
      'Engage/Expand providers based on their referral patterns and relationships',
    icon: faHandshake,
    disabled: false,
  },
  {
    label: 'Radius-Based Analysis',
    value: STRATEGY_TYPES.NOT_REFERRING,
    description:
      'Understand a provider or site’s “capture rate” in a given radius around their primary address',
    icon: faHandshakeSlash,
    disabled: false,
  },
  {
    label: 'Patient Journey',
    value: STRATEGY_TYPES.PATIENT_JOURNEY,
    description:
      'Engage/Expand providers based on patient journey and care pathway analysis',
    icon: faRoute,
    disabled: false,
  },
  {
    label: 'Treating Volume',
    value: STRATEGY_TYPES.TREATING_VOLUME,
    description:
      'Engage/Expand providers based on detailed volume and service data',
    icon: faChartLineDown,
    disabled: false,
  },
];

export const ENGAGE_STRATEGY_BUTTONS: StrategyButton[] =
  ENGAGE_STRATEGY_BUTTONS_UNSORTED.sort((a, b) => {
    // Sort by enabled (false) before disabled (true)
    if (a.disabled !== b.disabled) {
      return a.disabled ? 1 : -1;
    }

    // If both have same disabled status, maintain original order
    return 0;
  });
